import { theme as antdTheme, ThemeConfig } from 'antd';
import type { ComponentToken as MenuComponentToken } from 'antd/es/menu/style';

export type { MenuComponentToken };

const FONT_FAMILY_MAIN = "'Roboto', sans-serif";
const FONT_SIZE_BASE = 14;
const BORDER_RADIUS_BASE = 8;

export const darkTheme: Partial<ThemeConfig> = {
  algorithm: [antdTheme.darkAlgorithm],
  token: {
    colorPrimary: '#1668dc',
    colorInfo: '#2f83dc',
    colorError: '#dc4446',
    colorTextBase: '#fff',
  },
}

export const lightTheme: ThemeConfig = {
  cssVar: { prefix: 'bc', key: 'light-theme' },

  token: {
    // Primary & Info (different blues)
    colorPrimary: '#0092FF',
    colorInfo: '#3496FF',

    // Error (orange-red per design spec)
    colorError: '#F36B10',
    colorSuccess: '#2CB592',
    // Success & Warning: use antd default algorithms (green/gold presets per design spec)

    // Brand-blue text base — drives all text, fill, and spotlight tokens
    colorTextBase: '#002952',
    colorTextPlaceholder: '#6F8CAE',

    // Typography
    fontFamily: FONT_FAMILY_MAIN,
    fontSize: FONT_SIZE_BASE,

    // Sizing & Radius
    borderRadius: BORDER_RADIUS_BASE,
  },

  components: {
    Checkbox: {
      controlInteractiveSize: 18,
    },
    Input: {
      fontSizeLG: 14,
    },
    Select: {
      optionPadding: '8px 12px',
      multipleItemBg: 'none',
    },
    TreeSelect: {
      titleHeight: 32,
    },
    Layout: {
      headerHeight: 56,
      headerBg: '#ffffff',
      headerPadding: '0 24px',
    },
    Steps: {
      navArrowColor: 'transparent',
    },
    Tag: {
      borderRadiusSM: 4,
    },
    Table: {
      cellFontSizeSM: 12
    },
  },
};

/** Menu tokens for BetterComp gradient active indicator on light headers. */
const headerMenuTokens: Partial<MenuComponentToken> = {
  horizontalItemSelectedBg: 'var(--bc-color-fill-tertiary)',
  activeBarHeight: 3,
};

/** Custom token preset for the Bettercomp brand gradient (Rich Orange to Gold). */
export const brandGradientTokens = {
  brandGradientStart: '#F24E1A',
  brandGradientEnd: '#FFA700',
} as const;

export const gradientHeaderCustomTokens = {
  selectedMenuItemBorderColor: `linear-gradient(90deg, ${brandGradientTokens.brandGradientStart}, ${brandGradientTokens.brandGradientEnd})`
}

export const blueGradientHeaderTheme: ThemeConfig = {
  algorithm: [antdTheme.darkAlgorithm],
  token: {
    colorTextBase: '#ffffff',
    colorTextPlaceholder: '#ffffff',
    colorBgBase: '#002952',
    colorBgElevated: '#002952',
    colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
  },
  components: {
    Button: {
      textTextColor: '#ffffff',
    },
    Layout: {
      headerBg: '#002952',
      headerColor: '#ffffff',
    },
    Menu: {
      ...headerMenuTokens,
      horizontalItemSelectedColor: '#ffffff',
      itemColor: 'rgba(255,255,255,0.65)',
      itemSelectedColor: '#ffffff',
      subMenuItemSelectedColor: '#ffffff',
      itemBg: '#002952',
    },
  },
};

/** Combined theme for light header with BetterComp gradient indicator. */
export const gradientHeaderTheme: ThemeConfig = {
  components: {
    Menu: {
      ...headerMenuTokens,
      horizontalItemSelectedColor: 'var(--bc-color-text)',
      subMenuItemSelectedColor: 'var(--bc-color-text)',
    },
  },
};

export const defaultHeaderTheme = {
  components: {
    Menu: headerMenuTokens
  }
}

/** @deprecated Use `brandGradientTokens` instead. */
export const aiGradientTokens = {
  aiGradientStart: brandGradientTokens.brandGradientStart,
  aiGradientEnd: brandGradientTokens.brandGradientEnd,
} as const;

export function createBrandTheme(
  overrides?: Partial<ThemeConfig>,
): ThemeConfig {
  if (!overrides) {
    return lightTheme;
  }

  return {
    ...lightTheme,
    ...overrides,
    token: {
      ...lightTheme.token,
      ...overrides.token,
    },
    components: {
      ...lightTheme.components,
      ...overrides.components,
    },
  };
}
